<ion-content class="paywall ion-padding">
  <!-- One full-cover progress state for the whole paywall: while prices load
       (native catalog + live store fetch) AND while an action is in flight
       (subscribe / restore). The spinner never sits on a single button —
       an inline button spinner reads as "only that button is working" and is
       confusing when the whole modal is busy.

       The body is REPLACED by (not overlaid with) the loader: an absolute
       overlay inside ion-content only spans one viewport-height box, so a modal
       taller than the screen left its tail (the "Not now" button + legal text)
       poking out below the overlay. Rendering just the loader removes the tall
       content entirely, so nothing can escape it. -->
  @if (showLoadingOverlay()) {
    <div class="paywall__price-loading" role="status" aria-live="polite">
      <ion-spinner name="crescent"></ion-spinner>
      <p>{{ overlayMessage }}</p>
    </div>
  } @else {
  <div class="paywall__close" [class.paywall__close--back]="step() !== 'plan'">
    @if (step() !== 'plan') {
      <!-- Past the first step the X becomes Back: closing outright would throw
           away a plan choice the buyer has already made. -->
      <ion-button fill="clear" size="small" (click)="back()">
        <ion-icon slot="start" name="arrow-back-outline" aria-hidden="true"></ion-icon>
        {{ timelineCopy.Back }}
      </ion-button>
    } @else {
      <ion-button fill="clear" size="small" (click)="dismiss()" aria-label="Close">
        <ion-icon slot="icon-only" name="close-outline"></ion-icon>
      </ion-button>
    }
  </div>

  @if (step() === 'plan') {
  <div class="paywall__head">
    <h2 class="paywall__title">{{ heading }}</h2>
    <p class="paywall__msg">{{ message }}</p>
  </div>

  <!-- Two columns where the width allows: the value props are short, and a
       single tall column pushed the price and the timeline below the fold. -->
  <ul class="paywall__bullets">
    @for (b of copy.bullets; track b) {
      <li>
        <ion-icon name="checkmark-outline" aria-hidden="true"></ion-icon>
        <span>{{ b }}</span>
      </li>
    }
  </ul>

  <!-- Last resort (ADR-0037): nothing is purchasable on this platform — show a
       clear message instead of defaulting to any plan/gateway. -->
  @if (plansUnavailable()) {
    <p class="paywall__unavailable">{{ plansUnavailableMsg }}</p>
  }

  <!-- Native price state (backend-resolved live store price): no connectivity, or
       online but the store price couldn't be resolved. Distinct from "no plans". -->
  @if (pricingStateMsg()) {
    <p class="paywall__unavailable">{{ pricingStateMsg() }}</p>
  }

  <!-- What the first days cost, above the plans it applies to. No price here:
       the plans below are still selectable, so any figure would go stale on the
       next tap — they each carry their own. -->
  @if (trialLeadIn(); as lead) {
    <p class="paywall__trial-lead">{{ lead }}</p>
  }

  <!-- Price/plans: web always; native when signed-in with purchasable plans. The
       buy flow is decided by the resolved gateway in the initiate response. -->
  @if (showPrice) {
    @if (plans().length) {
      <!-- Guests can pre-select a plan too (they can't buy yet, but the choice is
           stored with the sign-up intent so checkout resumes with the SAME plan
           they picked here — no monthly-vs-annual surprise on the pay screen). -->
      <ion-radio-group
        [value]="selectedPlanId()"
        (ionChange)="onSelect($any($event).detail.value)"
      >
        @for (p of plans(); track p.id) {
          <div class="paywall__plan">
            <ion-radio [value]="p.id" labelPlacement="end" justify="start">
              <!-- Two lines, not one: the name, currency-prefixed price and the
                   billing period together overflow a phone-width radio label and
                   the tail was being ellipsised away — which silently dropped the
                   "/ month" suffix that Guideline 3.1.2(c) requires to be
                   visible. Stacking them keeps every part on screen. -->
              <span class="paywall__plan-label">
                <span class="paywall__plan-name">{{ p.name }}</span>
                <span class="paywall__plan-terms">
                  @if (trialLabel(p)) {
                    <span class="paywall__plan-trial">{{ trialLabel(p) }}</span>
                  }
                  @if (p.priceAvailable === false) {
                    <span class="paywall__plan-price paywall__plan-price--unavailable">
                      {{ priceUnavailableInlineMsg }}
                    </span>
                  } @else {
                    @if (listPriceLabel(p)) {
                      <span class="paywall__plan-was">{{ listPriceLabel(p) }}</span>
                    }
                    <span class="paywall__plan-price">{{ formatPrice(p) }}</span>
                  }
                  @if (frequencyLabel(p)) {
                    <span class="paywall__plan-frequency">{{ frequencyLabel(p) }}</span>
                  }
                  @if (discountBadge(p)) {
                    <span class="paywall__plan-discount">{{ discountBadge(p) }}</span>
                  }
                  @if (gstLabel(p)) {
                    <span class="paywall__plan-gst">{{ gstLabel(p) }}</span>
                  }
                  <!-- What happens to the cloud library if this plan lapses,
                       under the price it belongs to. Per plan because the grace
                       is a plan setting; absent entirely on a plan that deletes
                       nothing, rather than reassuring about a window that does
                       not exist. -->
                  @if (retentionLabel(p)) {
                    <span class="paywall__plan-retention">{{ retentionLabel(p) }}</span>
                  }
                  @if (rentalLabel(p)) {
                    <span class="paywall__plan-rental">{{ rentalLabel(p) }}</span>
                  }
                  @if (usageLabel(p)) {
                    <span class="paywall__plan-rental">{{ usageLabel(p) }}</span>
                  }
                </span>
              </span>
            </ion-radio>
          </div>
        }
      </ion-radio-group>
    }
  }

  <!-- What happens on which day, on the SAME screen as the price — there is
       nothing to tap past, and a guest deciding whether to sign up sees it too.
       Dates and amounts are the server's; the heading is omitted because the
       lead-in above the plans already introduced the trial. -->
  @if (hasTrial()) {
    <app-purchase-timeline class="paywall__timeline" [rows]="timelineRows()" />
  } @else if (disclosureLoading()) {
    <!-- A plan switch re-fetches the terms. Reserving the slot stops the CTA
         jumping up and back down under the buyer's thumb mid-tap. -->
    <div class="paywall__timeline-loading" role="status" aria-live="polite">
      <ion-spinner name="crescent"></ion-spinner>
    </div>
  }
  }

  <!-- Terms: its own step on native, where the modal is the whole flow. On web
       the checkout-confirmation route is this step and owns the country picker,
       so there is nothing to render here. -->
  @if (step() === 'terms' && isNative && disclosure(); as terms) {
    <!-- One line + a popup, shared with every other purchase surface. The tick
         only renders for a signed-in buyer; the terms line always does. -->
    <!-- The paywall lists the legal documents itself, below, in every state. -->
    <app-purchase-consent
      class="paywall__consent"
      [legalLinks]="false"
      [terms]="{
        consentRequired: state === 'needPlan' && terms.consentRequired,
        consentText: terms.consentText,
        summary: terms.summary,
        cancellationSummary: terms.cancellationSummary,
        refundSummary: terms.refundSummary,
        policyUrl: terms.policyUrl,
        warning: terms.warning,
      }"
      [busy]="busy()"
      [(accepted)]="consentGiven"
    />
  }

  <div class="paywall__actions">
    @if (state === 'guest') {
      <ion-button expand="block" (click)="signUp()" [disabled]="busy()">
        {{ createAccountLabel }}
      </ion-button>
      <ion-button expand="block" fill="outline" (click)="signIn()" [disabled]="busy()">
        {{ signInLabel }}
      </ion-button>
    } @else {
      <ion-button
        expand="block"
        (click)="primary()"
        [disabled]="primaryDisabled"
      >
        {{ primaryLabel }}
      </ion-button>
    }

    @if (showRestore) {
      <!-- App Store / Play requirement: restore prior purchases on this account. -->
      <ion-button expand="block" fill="clear" (click)="restore()" [disabled]="busy()">
        Restore purchases
      </ion-button>
    }

    @if (showSecondary) {
      <ion-button expand="block" fill="clear" (click)="dismiss()">Not now</ion-button>
    }
  </div>

  <!-- Only under a CTA that starts a trial: it answers "what am I committing
       to?", which is a different question once money moves today. -->
  @if (hasTrial() && state !== 'guest') {
    <p class="paywall__cta-note">{{ timelineCopy.TrialFootnote }}</p>
  }

  <!-- Team plans are web-only (ADR-0123); the link opens the plan-group chooser.
       Hidden on native and when the organisations feature is off. -->
  @if (showTeamLink && step() === 'plan') {
    <div class="paywall__team">
      <a class="paywall__team-link" (click)="openPlanChooser()">
        {{ teamLinkLabel }} <ion-icon name="arrow-forward-outline" aria-hidden="true"></ion-icon>
      </a>
    </div>
  }

  <!-- Auto-renew terms + functional legal links — required in-app on the
       subscription screen (App Store Guideline 3.1.2(c)). Shown in every state so
       an App Store reviewer always sees them, even in the guest state. -->
  <div class="paywall__legal">
    <!-- Per-file media caps: disclosed BEFORE purchase because a subscription
         does not raise them, so "unlimited" is never read as covering them. -->
    @if (mediaLimitsNote(); as note) {
      <p class="paywall__legal-terms">{{ note }}</p>
    }
    <p class="paywall__legal-terms">{{ autoRenewDisclosure }}</p>
    <div class="paywall__legal-links">
      @for (l of legalLinks; track l.url) {
        <a class="paywall__legal-link" (click)="openLegal(l.url)">{{ l.label }}</a>
      }
    </div>
  </div>
  }
</ion-content>
